import { useTranslate } from 'react-admin';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';

import { useResponsive } from 'src/hooks/use-responsive';

import { formatUsdc } from '../vaults/helpers';
import { OFFICIAL_VAULTS_NS as NS, REJECT_REASON_MAX } from './constants';
import type { ApplicationView } from './helpers';
import { ReplaceApprovalNotice } from './ReplaceApprovalNotice';
import type { RejectForm } from './useOfficialVaults';

/**
 * Reject an application (STORY-22 AC4, XBIT-4257): the reason is shown to the applicant. A stake already received is
 * owed back to its sender; until the refund lane exists (XBIT-4219) that refund is paid by hand, so the dialog says so.
 */
export const RejectDialog = ({
  form,
  stakeReceived = null,
  request = null,
  retryAfter,
}: {
  form: Pick<
    RejectForm,
    'vaultId' | 'reason' | 'error' | 'alert' | 'submitting' | 'closeDialog' | 'setReason' | 'submit'
  >;
  /** The manager's received stake (6 dp string), when one is in. */
  stakeReceived?: string | null;
  /** The application under review, for the replace-approval notice (T-02b). */
  request?: Pick<ApplicationView, 'review' | 'vaultState'> | null;
  retryAfter?: number;
}) => {
  const translate = useTranslate();
  const smDown = useResponsive('down', 'sm');
  const count = Array.from(form.reason.trim()).length;

  return (
    <Dialog
      open={form.vaultId !== null}
      onClose={form.submitting ? undefined : form.closeDialog}
      maxWidth="sm"
      fullWidth
      fullScreen={smDown}
    >
      <DialogTitle>{translate(`${NS}.review.rejectTitle`, { vaultId: form.vaultId })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <ReplaceApprovalNotice request={request} retryAfter={retryAfter} />
          {stakeReceived && (
            <Alert severity="warning">
              {translate(`${NS}.review.stakeOwedBack`, { amount: formatUsdc(stakeReceived) })}
            </Alert>
          )}
          {form.alert && (
            <Alert severity={form.alert.severity}>
              <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2 }}>
                {form.alert.messages.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </Stack>
            </Alert>
          )}
          <TextField
            required
            fullWidth
            multiline
            minRows={3}
            label={translate(`${NS}.review.reason`)}
            value={form.reason}
            error={Boolean(form.error)}
            helperText={[
              form.error && translate(form.error),
              translate(`${NS}.review.reasonHint`, { count, max: REJECT_REASON_MAX }),
            ]
              .filter(Boolean)
              .join(' · ')}
            onChange={(e) => form.setReason(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={form.closeDialog} disabled={form.submitting}>
          {translate(`${NS}.form.cancel`)}
        </Button>
        <LoadingButton
          variant="contained"
          color="error"
          onClick={form.submit}
          disabled={form.submitting}
          loading={form.submitting}
        >
          {translate(
            form.alert?.severity === 'error' ? `${NS}.review.retry` : `${NS}.review.reject`
          )}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
